'use strict';

define('vb/private/services/operationRefEndpointProvider',[
  'urijs/URI',
  'vb/private/log',
  'vb/private/configLoader',
  'vb/private/services/definition/openApiServiceDefFactory',
  'vb/private/services/operationRefEndpoint',
  'vb/private/services/endpointReference',
  'vb/private/services/serviceConstants',
  'vb/private/services/serviceResolverFactory',
  'vb/private/services/servicesManager',
  'vb/private/services/services',
  'vb/private/services/servicesLoader',
  'vb/private/services/serviceUtils',
  'vb/private/constants',
  'vb/private/urlMapper',
], (
  URI,
  Log,
  ConfigLoader,
  OpenApiServiceDefFactory,
  OperationRefEndpoint,
  EndpointReference,
  ServiceConstants,
  ServiceResolverFactory,
  ServicesManager,
  Services,
  ServicesLoader,
  ServiceUtils,
  Constants,
  UrlMapper,
) => {
  /**
   * @typedef {import('vb/private/stateManagement/container').default} Container
   * @typedef {import('vb/private/services/serviceResolverFactory').ServiceContext} ServiceContext
   */

  const logger = Log.getLogger('/vb/private/services/operationRefEndpointProvider');

  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions:
  // string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
  // We do not want to escape ".", "*", "{" and "}" as we need them
  const REG_EXP_ESCAPE = /[+?^$()|[\]\\]/g;
  const escapeRegExp = (string) => string.replace(REG_EXP_ESCAPE, '\\$&');

  // if it has "{}" then replace the param name within it with ".*" expression and try to match the RegExp
  const regexTemplates = /{(.*?)}/gi;

  // Cache the derived OpenAPI lookup data per catalog service so repeated matches stay cheap.
  // Most catalog services are objects, so use WeakMap for those entries. Catalogs can also contain primitive service
  // declarations, so keep a companion Map keyed by namespace/serviceName/value for those non-weak-map keys.
  const resolvedOpenApiUris = new WeakMap();
  const resolvedOpenApiComparators = new WeakMap();
  const resolvedOpenApiUriValues = new Map();
  const resolvedOpenApiComparatorValues = new Map();

  /**
   * Builds a cache key for catalog service lookup data.
   * @param {Object|string} service Catalog service declaration
   * @param {string} serviceName Catalog service name
   * @param {string} namespace Namespace context for the declaration
   * @returns {Object|string} Object service declarations are returned as-is; primitives use a namespaced string key
   */
  const getServiceCacheKey = (service, serviceName, namespace) => {
    if (service && (typeof service === 'object' || typeof service === 'function')) {
      return service;
    }
    return `${namespace || ''}:${serviceName}:${String(service)}`;
  };

  /**
   * Returns the cache pair that can hold the given key type.
   * @param {Object|string} cacheKey Cache key produced by getServiceCacheKey
   * @param {WeakMap} objectCache Cache for object keys
   * @param {Map} primitiveCache Cache for primitive keys
   * @returns {WeakMap|Map} cache appropriate for the key
   */
  const getServiceCache = (cacheKey, objectCache, primitiveCache) => (
    cacheKey && (typeof cacheKey === 'object' || typeof cacheKey === 'function') ? objectCache : primitiveCache
  );

  class OperationRefEndpointProvider {
    // typical mapping object
    // {
    //   "urlToFetch": "http://localhost:1988/webApps/ifixitfaster/api/incidents?technician=hcr",
    //   "url": "http://localhost:1988/webApps/ifixitfaster/api",
    //   "id": "incidents",
    //   "nameForProxy": "incidents",
    //   "headers": {
    //   "vb-allow-anonymous-access": true
    // },
    //   "baseUrl": "",
    //   "serviceName": "incidents"
    // }"

    /**
     * This is called by AbstractRestHelper._getEndpoint()
     *
     * @param {EndpointReference} endpointReference
     * @param {Container|ServiceContext} [serviceContext] - typically the container which defines the scope in which
     *                                                      the endpoint is being invoked.
     * @param [serverVariables]
     * @returns {Promise<OperationRefEndpoint>} Initialized instance of OperationRefEndpoint
     */
    static async getEndpoint(endpointReference, serviceContext, serverVariables) {
      logger.info('Getting OperationRef endpoint for reference \'', endpointReference, '\' starting from \'',
        serviceContext && serviceContext.extensionId, '\' namespace');

      let service = null;
      try {
        let def;

        // if app provided metadata that matched fetched URL use the result of that match
        let serviceName = await OperationRefEndpointProvider.getMDMappedServiceName(endpointReference, serviceContext);
        if (serviceName) {
          endpointReference.adjustEndpoint({ serviceName });
          def = await ServicesManager.FindDefinition(endpointReference, serverVariables, false);
        }

        // if operationRef has absolute URL of the openApi doc, use it to locate/load the service definition
        if (!def && endpointReference.serviceUrl) {
          const urlToFind = UrlMapper.normalizeUrl(endpointReference.serviceUrl);
          def = await ServicesManager.FindDefinitionByUrl(urlToFind, serviceContext);
        }

        // if we could not load/find service by its location, or by mapping in the service metadata
        // try reversed URL mapping
        if (!def) {
          // fall back to the old behavior
          const mapping = await OperationRefEndpointProvider.getMapping(endpointReference);
          if (mapping) {
            if (mapping.type === ServiceConstants.ExtensionTypes.SERVICES) {
              serviceName = mapping.serviceName;
            // } else { // URL was mapped to a backend
            // TODO: VBS-42386
            //    create ad-hoc empty service definition that uses information from the backend
            //    metadata: connection and transforms
            //
            }
          }

          if (serviceName) {
            endpointReference.adjustEndpoint({ serviceName });
            // Backward compatibility: disable service access check as we may be mapping to
            //  a wrong service which has not been exposed to extensions.
            //
            // For example: when an LOV maps to any of the services from a RAMP backend
            //  the service will have RAMP transform configured. That is good enough of the
            //  URL mapping as we will end up running correct transform. RAMP transforms do not
            //  need information about endpoint metadata, so it is OK not to have it resolved.
            def = await ServicesManager.FindDefinition(endpointReference, serverVariables, false);
          }
        }
        service = def && def.service;
      } catch (err) {
        logger.warn('Error while loading service of the endpoint: ', endpointReference, err);
      }

      const endpoint = new OperationRefEndpoint(endpointReference);
      return endpoint.init(service);
    }

    /**
     *
     * @param {EndpointReference} endpointReference
     * @returns {Promise<string|undefined>}
     * @private
     */
    static async getMDMappedServiceName(endpointReference, serviceContext) {
      const { url } = endpointReference;
      if (url) {
        const refNamespace = ServiceResolverFactory.getNamespace(serviceContext);
        const matched = await OperationRefEndpointProvider.findMatchInMetadata(url, refNamespace);

        if (matched) {
          logger.info('\'', matched.serviceName, '\' service found in \'',
            refNamespace, '\' metadata matching: ', endpointReference);
          return matched.serviceName;
        }
      }
      return undefined;
    }

    /**
     * Finds a "url mapping hint" object defined in the catalog metadata that is matching given URL.
     * The returned object includes the namespace where the matching hint was found so later catalog resolution can
     * reload the same service instead of resolving a same-named service from another visible namespace.
     *
     * @param {string} url URL to match
     * @param {string} refNamespace Namespace context in which the resolution should be done
     * @returns {Promise<object|null>}
     */
    static async findMatchInMetadata(url, refNamespace) {
      const metadata = await ConfigLoader.catalogRegistry.getAllMetadata(refNamespace);

      // search one namespace at a time
      const matched = Object.keys(metadata).reduce(async (currMatchPromise, ns) => {
        const currentMatch = await currMatchPromise;
        const match = currentMatch
          || await OperationRefEndpointProvider.findMatch(url, ns, metadata[ns].urlMappingHints);
        return match && Object.assign({}, match, { namespace: match.namespace || ns });
      },
      Promise.resolve(null));

      return matched;
    }

    /**
     * Finds an object defined in the list of metadata objects that is matching given URL.
     * If necessary it resolves the vb-catalog:// type of URL in the given metadata before
     * matching it with the URL.
     *
     * @param {string} url URL to match
     * @param {string} namespace Namespace context in which the resolution should be done
     * @param {object[]} [metadata] Metadata objects to match. Their 'url' property is used for matching
     * @returns {Promise<object|null>} Promise of matched metadata object or null value
     */
    static async findMatch(url, namespace, metadata = []) {
      const needsResolution = metadata.some((map) => !map.resolvedUrl);
      // we want to avoid repeated resolution of vb-catalog:// URLs
      // we do it only once and then store the resolved value
      if (needsResolution) {
        const handler = ConfigLoader.protocolRegistry.getHandler(Constants.VbProtocols.CATALOG);
        const resolved = await handler.resolveUrls(metadata
          .map((mapping) => mapping && (mapping.resolvedUrl || mapping.url)), namespace);
        resolved.forEach((resolvedUrl, index) => {
          // eslint-disable-next-line no-param-reassign
          metadata[index].resolvedUrl = resolvedUrl;
        });
      }
      return metadata.find((hint) => (hint.resolvedUrl
        && OperationRefEndpointProvider.matches(url, hint.resolvedUrl)
        && hint.serviceName));
    }

    /**
     * Checks if the resolved URL matches given URL. THe two URLs match if the beginning of the URL
     * matches the resolved URL. Resolved URL may contain arbitrary template fragments, eg. "{version}"
     * in "vb-catalog://backends/extA:backendFoo/{version}/data".
     *
     * @param {string} url
     * @param {string} resolvedUrl
     * @returns {boolean}
     */
    static matches(url, resolvedUrl) {
      if (url.startsWith(resolvedUrl)) {
        return true;
      }

      // if it has "{}" then replace the param name within it with ".*" expression and try to match the RegExp
      const regExpUrl = escapeRegExp(resolvedUrl.replaceAll(regexTemplates, '.*'));
      return (new RegExp(regExpUrl)).test(url);
    }

    /**
     * Finds a Service object defined in the catalog services that matches the given operationRef.
     * Searches visible namespaces in precedence order until a matching service is found.
     *
     * @param {string} operationRef URL to match
     * @param {string} refNamespace Namespace context in which the resolution should be done
     * @returns {Promise<Object|null>} Promise of matched service name and namespace, or null value
     */
    static async findMatchInCatalog(operationRef, refNamespace) {
      const services = await ConfigLoader.catalogRegistry.getAllServices(refNamespace);
      const { openApi } = EndpointReference.processOperationRef(operationRef);
      const normalizedOpenApi = OperationRefEndpointProvider.normalizeOpenApiUrl(openApi);

      const reducer = async (currMatchPromise, [namespace, namespaceServices]) => {
        const currentMatch = await currMatchPromise;
        if (currentMatch) {
          return currentMatch;
        }
        return OperationRefEndpointProvider.findMatchInServices(normalizedOpenApi, namespace, namespaceServices);
      };

      // search one namespace at a time
      return Object.entries(services).reduce(reducer, Promise.resolve(null));
    }

    /**
     * Finds a Service object defined in the catalog services that matches the given OpenAPI document URL.
     * Searches services in a namespace until one matches the OpenAPI document URL.
     *
     * @param {string} openApiUrlToMatch OpenAPI document URL to match
     * @param {string} namespace Namespace context in which the resolution should be done
     * @param {Object} services Services objects to match against
     * @returns {Promise<Object|null>} Promise of matched service name and namespace, or null value
     */
    static async findMatchInServices(openApiUrlToMatch, namespace, services = {}) {
      const reducer = async (currMatchPromise, [serviceName, service]) => {
        const currentMatch = await currMatchPromise;
        if (currentMatch) {
          return currentMatch;
        }

        // Compare against the effective OpenAPI fetch URL, not the raw vb-catalog service location.
        const matches = await OperationRefEndpointProvider.resolveServiceOpenApi(openApiUrlToMatch, serviceName,
          service, namespace);
        if (matches) {
          return { serviceName, namespace };
        }

        return null;
      };

      // search one namespace service at a time
      return Object.entries(services).reduce(reducer, Promise.resolve(null));
    }

    /**
     * Normalizes an OpenAPI document URL for catalog matching.
     *
     * @param {string} url OpenAPI document URL to normalize
     * @returns {string} Normalized URL or the original value if normalization fails
     */
    static normalizeOpenApiUrl(url) {
      try {
        return UrlMapper.normalizeUrl(url);
      } catch (error) {
        if (error) {
          return url;
        }
        return url;
      }
    }

    /**
     * Resolves and caches the effective OpenAPI document URL for a catalog service.
     *
     * @param {string} openApiUrlToMatch OpenAPI document URL to match
     * @param {string} serviceName Catalog service name
     * @param {Object} service Catalog service object used as the cache key
     * @param {string} namespace Namespace context in which the service should be resolved
     * @returns {Promise<boolean>} Promise of resolved OpenAPI document URL or null value
     */
    static async resolveServiceOpenApi(openApiUrlToMatch, serviceName, service, namespace) {
      let resolvedOpenApiUri = null;
      const cacheKey = getServiceCacheKey(service, serviceName, namespace);
      const uriCache = getServiceCache(cacheKey, resolvedOpenApiUris, resolvedOpenApiUriValues);

      if (uriCache.has(cacheKey)) {
        resolvedOpenApiUri = uriCache.get(cacheKey);
      } else {
        try {
          // eslint-disable-next-line max-len
          const catalogUrl = `${Constants.VbProtocols.CATALOG}://${ServiceConstants.ExtensionTypes.SERVICES}/${serviceName}`;
          const catalogInfo = await ServicesLoader.getCatalogExtensions(
            ConfigLoader.protocolRegistry,
            catalogUrl,
            serviceName,
            namespace,
            undefined,
            undefined,
            true, // skipTrapExtensions
            true, // useUrlMapping
          );
          let resolvedOpenApiUrl = catalogInfo && catalogInfo.url;

          const metadata = catalogInfo && catalogInfo.services && catalogInfo.services.metadata;
          // OpenAPI metadata can move the document fetch away from the resolved service root.
          if (resolvedOpenApiUrl && metadata) {
            resolvedOpenApiUrl = ServiceUtils.getServiceUrlFromMetadata(resolvedOpenApiUrl, metadata);
          }

          resolvedOpenApiUrl = resolvedOpenApiUrl
            ? OperationRefEndpointProvider.normalizeOpenApiUrl(resolvedOpenApiUrl)
            : null;

          // remember null if we couldn't resolve, so we can use uriCache.has() and not check again.
          resolvedOpenApiUri = resolvedOpenApiUrl ? new URI(resolvedOpenApiUrl) : null;
          uriCache.set(cacheKey, resolvedOpenApiUri);
        } catch (error) {
          logger.warn('Error while resolving catalogInfo for service: ', serviceName, error);
          return false;
        }
      }

      if (!resolvedOpenApiUri) {
        return false;
      }

      // Compare the requested OpenAPI URL with the resolved URL for a catalog service.
      const comparatorCache = getServiceCache(cacheKey, resolvedOpenApiComparators, resolvedOpenApiComparatorValues);
      let comparator = comparatorCache.get(cacheKey);
      if (!comparator) {
        comparator = OpenApiServiceDefFactory.getComparator(resolvedOpenApiUri);
        comparatorCache.set(cacheKey, comparator);
      }

      return comparator(resolvedOpenApiUri, openApiUrlToMatch);
    }

    /**
     * try to map the url to a catalog service, or already-opened openapi3/swagger
     * @param {EndpointReference} endpointReference
     * @returns {Promise<*>}
     */
    static getMapping(endpointReference) {
      return ConfigLoader.urlMapper.getUrlMapping(endpointReference.url);
    }

    /**
     * creates a Services model, for the Endpoint that represents the raw, unmapped URL.
     * @param name
     * @param operationRefUrl
     * @returns {Services}
     */
    static createServicesModel(name, operationRefUrl) {
      const servicesOptions = {
        application: undefined,
        namespace: Constants.ExtensionNamespaces.BASE,
        relativePath: '',
        serviceFileMap: { [name]: operationRefUrl },
        isUnrestrictedRelative: false,
        expressionContext: {},
        protocolRegistry: ConfigLoader.protocolRegistry,
        extensionRegistryGetter: undefined,
      };

      return new Services(servicesOptions);
    }
  }

  return OperationRefEndpointProvider;
});

